import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { Observable, Subject, of, timer } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ProduktDTO } from "~app/common/models/produkt/ProduktDTO";
import { ProduktspecifikationDTO } from "~app/common/models/produkt/ProduktspecifikationDTO";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { ProduktService } from "../../produkt.service";
import { ProduktDetailTexts } from "../produkt-detail.texts";
import { FormMode, IProduktspecifikationItems } from "../produkt-details.models";
import { ProduktDetailProduktspecifikationValidation } from "./produkt-detail-produktspecifikation.validation";

@Component({
    selector: "rask-produkt-detail-produktspecifikation",
    templateUrl: "./produkt-detail-produktspecifikation.component.html",
    styleUrls: ["./produkt-detail-produktspecifikation.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SelectorComponent,
        DatepickerComponent
    ]
})
export class ProduktDetailProduktspecifikationComponent implements OnInit, OnDestroy, OnChanges {
    form: UntypedFormGroup;
    @Input() formMode: FormMode;
    @Input() produkt: ProduktDTO;
    @Input() produktspecifikation: ProduktspecifikationDTO;
    produktspecifikationerItems: Observable<IProduktspecifikationItems[]>;
    @Input() rootForm: UntypedFormGroup;
    @Input() onProduktspecifikationChanged: (produktspecifikation: ProduktspecifikationDTO) => void;
    texts = ProduktDetailTexts.texts;
    validation: IValidationScope;
    validationContext: IValidationContext;

    private readonly busyService = inject(BusyService);
    private readonly formBinder = inject(FormBinderService);
    private readonly formId = "produkt-detail-produktspecifikation";
    private readonly produktService = inject(ProduktService);
    private readonly unsubscribe$: Subject<void>;

    constructor() {
        this.unsubscribe$ = new Subject<void>();
    }

    get produktspecifikationDisabled(): boolean {
        return this.formMode === FormMode.New || this.rootForm.dirty;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.shouldRecreateForm(changes)) {
            return;
        }

        this.destroyForm();
        this.createForm();
    }

    ngOnDestroy(): void {
        this.destroyForm();
    }

    ngOnInit(): void {
        this.createForm();
    }

    onProduktspecifikationChange($event): void {
        if (!this.produkt.produktspecifikation || $event === null || $event === undefined) {
            return;
        }

        const worker = this.busyService.addWork(
            this.produktService.getProduktspecifikationById(this.produkt.produktspecifikation)
        );

        worker.subscribe((result) => {
            this.produktspecifikation = result.produktspecifikation;
            if (this.onProduktspecifikationChanged) {
                this.onProduktspecifikationChanged(this.produktspecifikation);
            }
        });
    }

    produktspecifikationFormatter = (item: IProduktspecifikationItems): number => item ? item.id : undefined;

    produktspecifikationViewFormatter = (item: IProduktspecifikationItems): string => item ? `${item.giltigFrom} - ${item.giltigTom}` : "";

    private createForm(): void {
        const items = this.produkt.produktspecifikationer ? this.produkt.produktspecifikationer : [];

        this.produktspecifikationerItems = of(
            items.map((x) => ({
                id: x.id,
                giltigFrom: DateUtils.shortDateString(x.giltigFrom),
                giltigTom: x.giltigTom ? DateUtils.shortDateString(x.giltigTom) : ""
            }))
        );

        this.form = this.formBinder.CreateGroup(this.produktspecifikation);

        this.validationContext = ProduktDetailProduktspecifikationValidation.init(this.produktspecifikation);
        this.validationContext.scope(ValidationScopes.spara).subscribe(v => this.validation = v);
        this.formBinder.attachValidation(this.validationContext.current, this.form);

        this.form.get("giltigFrom").valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(newVal => {
                const oldVal = this.produktspecifikation.giltigFrom;
                timer(0).subscribe(() => {
                    this.validationContext.current.rules.forEach(rule => {
                        rule.validateModel();
                    });
                    if (oldVal !== newVal) {
                        this.form.get("giltigFrom").updateValueAndValidity({ onlySelf: false,
                            emitEvent: true });
                    }
                });
            });

        this.form.get("giltigTom").valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(newVal => {
                const oldVal = this.produktspecifikation.giltigTom;
                timer(0).subscribe(() => {
                    this.validationContext.current.rules.forEach(rule => {
                        rule.validateModel();
                    });
                    if (oldVal !== newVal) {
                        this.form.get("giltigTom").updateValueAndValidity({ onlySelf: false,
                            emitEvent: true });
                    }
                });
            });

        this.form.markAsPristine();

        this.rootForm.addControl(this.formId, this.form);
    }

    private destroyForm(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.formBinder.detachFromRootForm(this.rootForm, this.formId);
    }

    private shouldRecreateForm(changes: SimpleChanges): boolean {
        if (changes.produkt && changes.produkt.previousValue &&
            changes.produkt.previousValue.rowVersion !== changes.produkt.currentValue.rowVersion) {
            return true;
        }

        if (changes.produktspecifikation && changes.produktspecifikation.previousValue &&
            changes.produktspecifikation.previousValue.rowVersion !== changes.produktspecifikation.currentValue.rowVersion) {
            return true;
        }

        return false;
    }
}
